import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './tufts-icon-link-group.css.lit';
import { ResizeController } from '../../controllers/resize-controller';
import { SlotNGController } from '../../controllers/slot-ng-controller';
import tuftsHeadingCssLit from '../tufts-heading/tufts-heading.css.lit';
import { classMap } from 'lit/directives/class-map.js';

/**
 * The TuftsIconLinkGroup component displays a group of links with icons.
 *
 * @element tufts-icon-link-group
 *
 * @slot - The default slot for the component, which should contain `tufts-icon-link` elements.
 *
 * @attr {String} max-columns - The maximum number of columns for the component.
 */
@customElement('tufts-icon-link-group')
export class TuftsIconLinkGroup extends OutlineElement {
  static styles: CSSResultGroup = [tuftsHeadingCssLit, componentStyles];

  /**
   * The SlotNGController instance for the component.
   */
  ngSlot = new SlotNGController(this);

  /**
   * The ResizeController instance for the component.
   */
  resizeController: ResizeController = new ResizeController(this, {
    breakpoints: [769],
  });

  /**
   * A boolean indicating whether the component is in mobile view.
   */
  isMobile = true;

  /**
   * The maximum number of columns for the component.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'max-columns',
  })
  maxColumns = '1';

  /**
   * The version for the component.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'version',
  })
  version: string;

  /**
   * Returns a filler template for the list item element, to prevent an odd items which looks broken.
   * If the number of slotted nodes is odd, returns an empty list item element.
   * Otherwise, returns an empty template.
   *
   * @returns {TemplateResult} - The filler template for the list item element.
   */
  liFillerTemplate(): TemplateResult {
    if (
      !this.isMobile &&
      this.maxColumns === '2' &&
      this.ngSlot.count() % 2 === 1
    ) {
      return html`<li></li>`;
    }
    return html``;
  }

  /**
   * Renders the component.
   *
   * @returns {TemplateResult} - The rendered template for the component.
   */
  render(): TemplateResult {
    this.style.setProperty('--max-columns', this.maxColumns);

    this.isMobile = this.resizeController.currentBreakpointRange === 0;
    const classes = {
      'tufts-icon-link-group': true,
      'mobile': this.isMobile,
      'two-col': !this.isMobile && this.maxColumns === '2',
    };
    return html`
      <div class=${this.version}>
        ${this.ngSlot.conditionalNGSlot('header', true, 'md')}
        <ul class="${classMap(classes)}">
          ${this.ngSlot.renderInShadow('', 'li')} ${this.liFillerTemplate()}
        </ul>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-icon-link-group': TuftsIconLinkGroup;
  }
}
